<script setup>
import { computed, ref } from 'vue';
import { useI18n } from 'vue-i18n';
import { triggerNodeAction } from '../nodeEventBus';

const props = defineProps({
  id: { type: String, default: '' },
  data: { type: Object, default: () => ({}) },
});

const { t } = useI18n();
const isEditingLabel = ref(false);
const isEditingDesc = ref(false);
const tempLabel = ref('');
const tempDesc = ref('');

const groupLabel = computed(
  () => props.data?.label || t('FLOW_BUILDER.SIDEBAR.GROUP')
);

const groupDescription = computed(() => props.data?.description || '');

const groupColor = computed(() => props.data?.color || 'slate');

const colorPalette = [
  {
    key: 'slate',
    class:
      'border-n-slate-6 dark:border-n-slate-8 bg-n-slate-2/30 dark:bg-n-slate-3/20',
    dot: 'bg-n-slate-5',
  },
  {
    key: 'blue',
    class: 'border-n-blue-5 bg-n-blue-1/20 dark:bg-n-blue-2/10',
    dot: 'bg-n-blue-5',
  },
  {
    key: 'green',
    class: 'border-n-green-5 bg-n-green-1/20 dark:bg-n-green-2/10',
    dot: 'bg-n-green-5',
  },
  {
    key: 'amber',
    class: 'border-n-amber-5 bg-n-amber-1/20 dark:bg-amber-2/10',
    dot: 'bg-n-amber-5',
  },
  {
    key: 'ruby',
    class: 'border-n-ruby-5 bg-n-ruby-1/20 dark:bg-n-ruby-2/10',
    dot: 'bg-n-ruby-5',
  },
  {
    key: 'violet',
    class: 'border-n-violet-5 bg-n-violet-1/20 dark:bg-violet-2/10',
    dot: 'bg-n-violet-5',
  },
  {
    key: 'indigo',
    class: 'border-n-indigo-5 bg-n-indigo-1/20 dark:bg-indigo-2/10',
    dot: 'bg-n-indigo-5',
  },
  {
    key: 'teal',
    class: 'border-n-teal-5 bg-n-teal-1/20 dark:bg-teal-2/10',
    dot: 'bg-n-teal-5',
  },
];

const isColorPickerOpen = ref(false);

const currentColorConfig = computed(
  () => colorPalette.find(c => c.key === groupColor.value) || colorPalette[0]
);

const selectColor = color => {
  isColorPickerOpen.value = false;
  triggerNodeAction('update', props.id, { color });
};

const startEditingLabel = () => {
  tempLabel.value = groupLabel.value;
  isEditingLabel.value = true;
};

const finishEditingLabel = () => {
  isEditingLabel.value = false;
  if (tempLabel.value !== groupLabel.value) {
    triggerNodeAction('update', props.id, { label: tempLabel.value });
  }
};

const startEditingDesc = () => {
  tempDesc.value = groupDescription.value;
  isEditingDesc.value = true;
};

const finishEditingDesc = () => {
  isEditingDesc.value = false;
  if (tempDesc.value !== groupDescription.value) {
    triggerNodeAction('update', props.id, { description: tempDesc.value });
  }
};

// --- Resize logic ---
const isResizing = ref(false);
const resizeStart = ref({ x: 0, y: 0, w: 0, h: 0 });

const onResize = event => {
  if (!isResizing.value) return;
  const dx = event.clientX - resizeStart.value.x;
  const dy = event.clientY - resizeStart.value.y;
  let newW = resizeStart.value.w;
  let newH = resizeStart.value.h;
  const dir = resizeStart.value.direction;
  if (dir.includes('e')) newW = Math.max(300, resizeStart.value.w + dx);
  if (dir.includes('s')) newH = Math.max(200, resizeStart.value.h + dy);
  triggerNodeAction('update', props.id, {
    resize: { width: `${newW}px`, height: `${newH}px` },
  });
};

const stopResize = () => {
  isResizing.value = false;
  window.removeEventListener('mousemove', onResize);
  window.removeEventListener('mouseup', stopResize);
};

const startResize = (event, direction) => {
  event.stopPropagation();
  event.preventDefault();
  isResizing.value = true;
  const node = event.target.closest('.flow-group-node');
  const wrapper = node.parentElement;
  const rect = wrapper.getBoundingClientRect();
  resizeStart.value = {
    x: event.clientX,
    y: event.clientY,
    w: rect.width,
    h: rect.height,
    direction,
  };
  window.addEventListener('mousemove', onResize);
  window.addEventListener('mouseup', stopResize);
};

const startResizeHandler = (event, direction) => startResize(event, direction);
</script>

<template>
  <div
    class="flow-group-node w-full h-full rounded-xl border-2 border-dashed transition-colors relative min-w-[300px] min-h-[200px] pointer-events-none"
    :class="currentColorConfig.class"
  >
    <!-- Group title bar -->
    <div
      class="flex items-center gap-2 px-3 py-2 border-b border-n-slate-5 dark:border-n-slate-7 rounded-t-xl bg-n-slate-2/50 dark:bg-n-slate-3/30 pointer-events-auto"
    >
      <span class="i-lucide-box text-sm text-n-blue-9" />
      <input
        v-if="isEditingLabel"
        v-model="tempLabel"
        type="text"
        class="flex-1 min-w-0 bg-transparent text-xs font-semibold text-n-slate-12 outline-none border-b border-n-blue-5"
        @blur="finishEditingLabel"
        @keydown.enter="finishEditingLabel"
        @keydown.esc="isEditingLabel = false"
      />
      <span
        v-else
        class="flex-1 min-w-0 text-xs font-semibold text-n-slate-12 cursor-text truncate"
        @dblclick="startEditingLabel"
      >
        {{ groupLabel }}
      </span>
      <button
        class="flex-shrink-0 w-8 h-8 rounded-md flex items-center justify-center text-n-slate-9 hover:text-n-blue-10 hover:bg-n-blue-1 dark:hover:bg-n-blue-2 transition-colors"
        :title="t('FLOW_BUILDER.GROUP.EDIT_NAME')"
        @click.stop="startEditingLabel"
      >
        <span class="i-lucide-pencil text-xs" />
      </button>
      <div class="relative flex-shrink-0">
        <button
          class="w-8 h-8 rounded-md flex items-center justify-center transition-colors hover:bg-n-slate-4 dark:hover:bg-n-slate-6"
          :title="t('FLOW_BUILDER.GROUP.CHANGE_COLOR')"
          @click.stop="isColorPickerOpen = !isColorPickerOpen"
        >
          <span class="w-4 h-4 rounded-full" :class="currentColorConfig.dot" />
        </button>
        <div
          v-if="isColorPickerOpen"
          class="absolute top-9 right-0 z-[9999] flex flex-row gap-2 p-2 rounded-lg border border-n-slate-5 dark:border-n-slate-7 bg-n-slate-1 dark:bg-n-slate-3 shadow-lg"
        >
          <button
            v-for="color in colorPalette"
            :key="color.key"
            class="w-6 h-6 rounded-full border-2 transition-transform hover:scale-110"
            :class="[
              color.dot,
              groupColor === color.key
                ? 'border-n-slate-12 dark:border-n-slate-1'
                : 'border-transparent',
            ]"
            @click.stop="selectColor(color.key)"
          />
        </div>
      </div>
      <button
        class="flex-shrink-0 w-8 h-8 rounded-md flex items-center justify-center text-n-ruby-11 hover:bg-n-ruby-1 dark:hover:bg-n-ruby-2 transition-colors"
        :title="t('FLOW_BUILDER.SIDEBAR.DELETE')"
        @click.stop="triggerNodeAction('delete', props.id)"
      >
        <span class="i-lucide-trash text-xs" />
      </button>
    </div>

    <!-- Description -->
    <div class="px-3 py-1.5 pointer-events-auto">
      <textarea
        v-if="isEditingDesc"
        v-model="tempDesc"
        rows="2"
        class="w-full bg-transparent text-[9px] text-n-slate-11 outline-none border border-n-slate-5 dark:border-n-slate-7 rounded px-1.5 py-1 resize-none"
        :placeholder="t('FLOW_BUILDER.GROUP.DESCRIPTION_PLACEHOLDER')"
        @blur="finishEditingDesc"
        @keydown.esc="isEditingDesc = false"
      />
      <p
        v-else-if="groupDescription"
        class="text-[9px] text-n-slate-11 cursor-text leading-tight"
        @dblclick="startEditingDesc"
      >
        {{ groupDescription }}
      </p>
      <button
        v-else
        class="text-[9px] text-n-slate-9 hover:text-n-blue-10 transition-colors"
        @click.stop="startEditingDesc"
      >
        {{ t('FLOW_BUILDER.GROUP.ADD_DESCRIPTION') }}
      </button>
    </div>

    <!-- Resize handles -->
    <div
      class="absolute bottom-0 right-0 w-4 h-4 cursor-nwse-resize pointer-events-auto"
      @mousedown.stop="startResizeHandler($event, 'se')"
    >
      <span class="i-lucide-corner-right-down text-xs text-n-slate-8" />
    </div>
    <div
      class="absolute bottom-0 left-0 right-0 h-1.5 cursor-ns-resize pointer-events-auto"
      @mousedown.stop="startResizeHandler($event, 's')"
    />
    <div
      class="absolute top-0 right-0 bottom-0 w-1.5 cursor-ew-resize pointer-events-auto"
      @mousedown.stop="startResizeHandler($event, 'e')"
    />
  </div>
</template>
